import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface MeModuleNav {
  label: string;
  icon?: string;
  path: string;
  order?: number;
}

export interface MeModule {
  id: string;
  nav: MeModuleNav[];
}

export interface MeResponse {
  discordId: string;
  username: string | null;
  avatar: string | null;
  role: 'owner' | 'admin' | 'support';
  permissions: string[];
  modules: MeModule[];
}

export interface AdminRow {
  discordId: string;
  role: 'owner' | 'admin' | 'support';
  addedByDiscordId: string | null;
  addedAt: string;
}

export interface FeatureFlagRow {
  moduleId: string;
  enabled: boolean;
  config: Record<string, unknown>;
  updatedAt: string;
}

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api<MeResponse>('/me'),
    retry: false,
  });
}

export function useAdmins() {
  return useQuery({
    queryKey: ['admins'],
    queryFn: () => api<AdminRow[]>('/admins'),
  });
}

export function useAddAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { discordId: string; role?: 'admin' | 'support' }) =>
      api<AdminRow>('/admins', { method: 'POST', body: JSON.stringify(vars) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admins'] }),
  });
}

export function useRemoveAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (discordId: string) =>
      api<void>(`/admins/${discordId}`, { method: 'DELETE', body: '{}' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admins'] }),
  });
}

export function useModules() {
  return useQuery({
    queryKey: ['modules'],
    queryFn: () => api<FeatureFlagRow[]>('/modules'),
  });
}

export function useToggleModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { moduleId: string; enabled: boolean }) =>
      api<FeatureFlagRow>(`/modules/${vars.moduleId}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: vars.enabled }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['modules'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: () => api<void>('/auth/logout', { method: 'POST', body: '{}' }),
  });
}

export interface ServerConfigRow {
  id: string;
  ownerDiscordId: string;
  name: string | null;
  archiveCategoryId: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useServerConfig() {
  return useQuery({
    queryKey: ['server-config'],
    queryFn: () => api<ServerConfigRow>('/server'),
  });
}

export function useUpdateServerConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: { archiveCategoryId?: string | null; name?: string | null }) =>
      api<ServerConfigRow>('/server', { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['server-config'] }),
  });
}

export interface AuditLogRow {
  id: string;
  actorDiscordId: string;
  action: string;
  target: Record<string, unknown>;
  createdAt: string;
}

export function useAuditLog(before?: string) {
  return useQuery({
    queryKey: ['audit-log', before ?? ''],
    queryFn: () => api<AuditLogRow[]>('/audit-log', { query: { before } }),
  });
}

export interface DashboardData {
  openTickets: number;
  closedTicketsLast30d: number;
  newUsersLast30d: number;
  ticketTimeseries: Array<{ day: string; opened: number; closed: number }>;
}

export function useDashboard() {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api<DashboardData>('/dashboard'),
    refetchInterval: 30_000,
  });
}

// ───────── Users module hooks ─────────

export interface UserRow {
  discordId: string;
  username: string | null;
  displayName: string | null;
  joinedServerAt: string | null;
  createdAt: string;
}

export function useUsers(q?: string) {
  return useQuery({
    queryKey: ['users', q ?? ''],
    queryFn: () => api<UserRow[]>('/users', { query: q ? { q } : {} }),
  });
}

export interface UserCard {
  user: {
    discordId: string;
    username: string | null;
    displayName: string | null;
    joinedServerAt: string | null;
  } | null;
  ticketCount: number;
  notes: Array<{
    id: string;
    authorDiscordId: string;
    note: string;
    createdAt: string;
  }>;
}

export function useUserCard(discordId: string) {
  return useQuery({
    queryKey: ['users', discordId, 'card'],
    queryFn: () => api<UserCard>(`/users/${discordId}`),
    enabled: Boolean(discordId),
  });
}

export function useAddNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ discordId, note }: { discordId: string; note: string }) =>
      api<{ ok: boolean }>(`/users/${discordId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ note }),
      }),
    onSuccess: (_, { discordId }) => {
      qc.invalidateQueries({ queryKey: ['users', discordId, 'card'] });
    },
  });
}

// ───────── Tickets hooks ─────────

export interface TicketCategoryRow {
  id: string;
  name: string;
  emoji: string | null;
  description: string | null;
  targetDiscordCategoryId: string | null;
  channelNameTemplate: string;
  welcomeEmbed: Record<string, unknown>;
  closePolicy: Record<string, unknown>;
  customFields: unknown[];
  supportRoleIds: string[];
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export function useTicketCategories() {
  return useQuery({
    queryKey: ['ticket-categories'],
    queryFn: () => api<TicketCategoryRow[]>('/ticket-categories'),
  });
}

export function useCreateTicketCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      emoji?: string;
      description?: string;
      targetDiscordCategoryId?: string;
    }) =>
      api<TicketCategoryRow>('/ticket-categories', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ticket-categories'] }),
  });
}

export function useUpdateTicketCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Partial<TicketCategoryRow> }) =>
      api<TicketCategoryRow>(`/ticket-categories/${vars.id}`, {
        method: 'PATCH',
        body: JSON.stringify(vars.patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ticket-categories'] }),
  });
}

export function useDeleteTicketCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/ticket-categories/${id}`, { method: 'DELETE', body: '{}' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ticket-categories'] }),
  });
}

export interface TicketPanelRow {
  id: string;
  channelId: string;
  messageId: string | null;
  embed: Record<string, unknown>;
  categoryIds: string[];
  customerLookupUrl: string | null;
  customerLookupSecretLastRotatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTicketPanelResponse extends TicketPanelRow {
  customerLookupSecret: string | null;
}

export function useTicketPanels() {
  return useQuery({
    queryKey: ['ticket-panels'],
    queryFn: () => api<TicketPanelRow[]>('/ticket-panels'),
  });
}

export function useCreateTicketPanel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      channelId: string;
      embed: Record<string, unknown>;
      categoryIds: string[];
      customerLookupUrl?: string | null;
      customerLookupSecret?: string | null;
    }) =>
      api<CreateTicketPanelResponse>('/ticket-panels', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ticket-panels'] }),
  });
}

export function useUpdateTicketPanel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      patch: Partial<TicketPanelRow> & { customerLookupSecret?: string | null };
    }) =>
      api<CreateTicketPanelResponse>(`/ticket-panels/${vars.id}`, {
        method: 'PATCH',
        body: JSON.stringify(vars.patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ticket-panels'] }),
  });
}

export function usePublishTicketPanel() {
  return useMutation({
    mutationFn: (id: string) =>
      api<{ requestId: string }>(`/ticket-panels/${id}/publish`, {
        method: 'POST',
        body: '{}',
      }),
  });
}

export function useDeleteTicketPanel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/ticket-panels/${id}`, { method: 'DELETE', body: '{}' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ticket-panels'] }),
  });
}

export function useRegeneratePanelLookupSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ customerLookupSecret: string }>(`/ticket-panels/${id}/regenerate-lookup-secret`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ticket-panels'] }),
  });
}

export interface TicketRow {
  id: string;
  categoryId: string;
  openerDiscordId: string;
  channelId: string;
  status: 'open' | 'closed' | 'archived';
  openedAt: string;
  closedAt: string | null;
  closedByDiscordId: string | null;
  closeReason: string | null;
  claimedByDiscordId: string | null;
  customFieldValues: Record<string, unknown>;
}

export interface TicketsFilters {
  status?: string;
  categoryId?: string;
  opener?: string;
}

export function useTickets(filters?: TicketsFilters) {
  return useQuery({
    queryKey: ['tickets', filters ?? {}],
    queryFn: () =>
      api<TicketRow[]>('/tickets', {
        query: filters as Record<string, string | undefined>,
      }),
  });
}

export function useTicket(id: string) {
  return useQuery({
    queryKey: ['tickets', id],
    queryFn: () =>
      api<
        TicketRow & {
          events: Array<{ id: string; kind: string; payload: unknown; createdAt: string }>;
          messages: Array<{
            id: string;
            authorDiscordId: string;
            content: string | null;
            createdAt: string;
          }>;
          transcript: unknown | null;
        }
      >(`/tickets/${id}`),
    enabled: Boolean(id),
  });
}

export function useCloseTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; reason?: string }) =>
      api<{ requestId: string }>(`/tickets/${vars.id}/close`, {
        method: 'POST',
        body: JSON.stringify({ reason: vars.reason }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['tickets', vars.id] });
    },
  });
}

// ───────── Role rules hooks ─────────

export interface RoleRuleRow {
  id: string;
  name: string;
  enabled: boolean;
  triggerKind: 'on_join' | 'time_on_server' | 'webhook_event';
  triggerConfig: Record<string, unknown>;
  roleId: string;
  webhookSecretLastRotatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRoleRuleResponse extends RoleRuleRow {
  webhookSecret: string | null;
}

export function useRoleRules() {
  return useQuery({
    queryKey: ['role-rules'],
    queryFn: () => api<RoleRuleRow[]>('/role-rules'),
  });
}

export function useCreateRoleRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      triggerKind: 'on_join' | 'time_on_server' | 'webhook_event';
      triggerConfig: Record<string, unknown>;
      roleId: string;
    }) =>
      api<CreateRoleRuleResponse>('/role-rules', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['role-rules'] }),
  });
}

export function useUpdateRoleRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Partial<RoleRuleRow> }) =>
      api<RoleRuleRow>(`/role-rules/${vars.id}`, {
        method: 'PATCH',
        body: JSON.stringify(vars.patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['role-rules'] }),
  });
}

export function useDeleteRoleRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/role-rules/${id}`, { method: 'DELETE', body: '{}' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['role-rules'] }),
  });
}

export function useRegenerateRoleRuleSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ webhookSecret: string }>(`/role-rules/${id}/regenerate-secret`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['role-rules'] }),
  });
}

// ───────── Announcements hooks ─────────

export interface AnnouncementRow {
  id: string;
  name: string;
  embed: Record<string, unknown>;
  targetChannelIds: string[];
  mention: string | null;
  status: 'draft' | 'scheduled' | 'sent' | 'failed';
  scheduledAt: string | null;
  sentAt: string | null;
  publishedMessages: { channelId: string; messageId: string }[];
  createdAt: string;
  updatedAt: string;
  createdByDiscordId: string;
}

export function useAnnouncements() {
  return useQuery({
    queryKey: ['announcements'],
    queryFn: () => api<AnnouncementRow[]>('/announcements'),
    // Bot updates publishedMessages / status asynchronously after a
    // publish-now or unpublish action — poll briefly so the UI catches
    // up without manual refresh.
    refetchInterval: 3000,
  });
}

// Helper: re-invalidates after a delay to catch async bot updates that
// the immediate post-action invalidation misses.
function scheduleDelayedInvalidate(qc: ReturnType<typeof useQueryClient>): void {
  setTimeout(() => qc.invalidateQueries({ queryKey: ['announcements'] }), 2500);
}

export function useCreateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      embed: Record<string, unknown>;
      targetChannelIds: string[];
      mention: string | null;
    }) => api<AnnouncementRow>('/announcements', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });
}

export function useUpdateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Partial<AnnouncementRow> }) =>
      api<AnnouncementRow>(`/announcements/${vars.id}`, {
        method: 'PATCH',
        body: JSON.stringify(vars.patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });
}

export function useDeleteAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; unpublish: boolean }) =>
      api<void>(`/announcements/${vars.id}?unpublish=${vars.unpublish ? 'true' : 'false'}`, {
        method: 'DELETE',
        body: '{}',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });
}

export function usePublishAnnouncementNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/announcements/${id}/publish-now`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['announcements'] });
      scheduleDelayedInvalidate(qc);
    },
  });
}

export function useScheduleAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; scheduledAt: string }) =>
      api<AnnouncementRow>(`/announcements/${vars.id}/schedule`, {
        method: 'POST',
        body: JSON.stringify({ scheduledAt: vars.scheduledAt }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });
}

export function useUnscheduleAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<AnnouncementRow>(`/announcements/${id}/unschedule`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });
}

export function useUnpublishAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/announcements/${id}/unpublish`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['announcements'] });
      scheduleDelayedInvalidate(qc);
    },
  });
}

// Discord guild metadata (from bot's Redis cache).
// channelType numeric values from discord.js ChannelType enum:
// 0 = GuildText, 2 = GuildVoice, 4 = GuildCategory, 5 = GuildAnnouncement,
// 13 = GuildStageVoice, 15 = GuildForum, 16 = GuildMedia
export const CHANNEL_TYPE = {
  GuildText: 0,
  GuildVoice: 2,
  GuildCategory: 4,
  GuildAnnouncement: 5,
  GuildStageVoice: 13,
  GuildForum: 15,
  GuildMedia: 16,
} as const;

export interface DiscordChannel {
  id: string;
  name: string;
  type: number;
  parentId: string | null;
  position: number;
}

export interface DiscordRole {
  id: string;
  name: string;
  color: number;
  position: number;
  managed: boolean;
}

export function useDiscordChannels() {
  return useQuery({
    queryKey: ['discord', 'channels'],
    queryFn: () => api<DiscordChannel[]>('/discord/channels'),
    staleTime: 30_000,
  });
}

export function useDiscordRoles() {
  return useQuery({
    queryKey: ['discord', 'roles'],
    queryFn: () => api<DiscordRole[]>('/discord/roles'),
    staleTime: 30_000,
  });
}

export interface GiveawayRow {
  id: string;
  name: string;
  prize: string;
  embed: Record<string, unknown>;
  channelId: string;
  messageId: string | null;
  winnerCount: number;
  requiredRoleId: string | null;
  mention: string | null;
  status: 'draft' | 'scheduled' | 'active' | 'ended' | 'cancelled';
  startAt: string | null;
  endsAt: string;
  winnerUserIds: string[];
  endedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdByDiscordId: string;
}

export type GiveawayDetail = GiveawayRow & { entryCount: number };

export function useGiveaways() {
  return useQuery({
    queryKey: ['giveaways'],
    queryFn: () => api<GiveawayRow[]>('/giveaways'),
    refetchInterval: 5000,
  });
}

// Detail query polls faster so the live entrant count + status stay current
// while the admin watches an active giveaway.
export function useGiveaway(id: string) {
  return useQuery({
    queryKey: ['giveaways', id],
    queryFn: () => api<GiveawayDetail>(`/giveaways/${id}`),
    refetchInterval: 3000,
    enabled: !!id,
  });
}

function invalidateGiveaways(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: ['giveaways'] });
}

export function useCreateGiveaway() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      prize: string;
      embed: Record<string, unknown>;
      channelId: string;
      winnerCount: number;
      endsAt: string;
      requiredRoleId: string | null;
      mention: string | null;
    }) => api<GiveawayRow>('/giveaways', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => invalidateGiveaways(qc),
  });
}

export function useUpdateGiveaway() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Partial<GiveawayRow> }) =>
      api<GiveawayRow>(`/giveaways/${vars.id}`, {
        method: 'PATCH',
        body: JSON.stringify(vars.patch),
      }),
    onSuccess: () => invalidateGiveaways(qc),
  });
}

export function useDeleteGiveaway() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; unpublish: boolean }) =>
      api<void>(`/giveaways/${vars.id}?unpublish=${vars.unpublish ? 'true' : 'false'}`, {
        method: 'DELETE',
        body: '{}',
      }),
    onSuccess: () => invalidateGiveaways(qc),
  });
}

export function usePublishGiveawayNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/giveaways/${id}/publish-now`, { method: 'POST', body: '{}' }),
    onSuccess: () => invalidateGiveaways(qc),
  });
}

export function useScheduleGiveaway() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; startAt: string }) =>
      api<GiveawayRow>(`/giveaways/${vars.id}/schedule`, {
        method: 'POST',
        body: JSON.stringify({ startAt: vars.startAt }),
      }),
    onSuccess: () => invalidateGiveaways(qc),
  });
}

export function useUnscheduleGiveaway() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<GiveawayRow>(`/giveaways/${id}/unschedule`, { method: 'POST', body: '{}' }),
    onSuccess: () => invalidateGiveaways(qc),
  });
}

export function useEndGiveawayNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/giveaways/${id}/end-now`, { method: 'POST', body: '{}' }),
    onSuccess: () => invalidateGiveaways(qc),
  });
}

export function useRerollGiveaway() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; count?: number }) =>
      api<{ ok: boolean }>(`/giveaways/${vars.id}/reroll`, {
        method: 'POST',
        body: JSON.stringify({ count: vars.count }),
      }),
    onSuccess: () => invalidateGiveaways(qc),
  });
}
